import React, { useEffect, useState } from 'react';
import { db } from '../config/firebase/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { Bar, Pie, Doughnut, Line, Radar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement, RadialLinearScale } from 'chart.js';
import { 
  Container, Typography, Card, CardContent, Grid, Box, Paper, Divider, 
  FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Rating, Chip, IconButton, Collapse,
  Tab, Tabs, Pagination, Button, Stack, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TourIcon from '@mui/icons-material/Tour';
import PersonIcon from '@mui/icons-material/Person';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement, RadialLinearScale);

const FormReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });
  const [touristTypeData, setTouristTypeData] = useState({
    labels: [],
    datasets: [],
  });
  const [tourPopularityData, setTourPopularityData] = useState({
    labels: [],
    datasets: [],
  });
  const [trendData, setTrendData] = useState({
    labels: [],
    datasets: [],
  });
  const [tourComparisonData, setTourComparisonData] = useState({
    labels: [],
    datasets: [],
  });
  const [selectedTrendMetric, setSelectedTrendMetric] = useState('overallSatisfaction');
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState(null);
  const [filterTouristType, setFilterTouristType] = useState('All');
  const [selectedToursForComparison, setSelectedToursForComparison] = useState([]);
  const [comparisonView, setComparisonView] = useState('radar'); // 'radar' or 'bar'

  const rowsPerPage = 5;

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'surveys'));
        const reviewsData = querySnapshot.docs.map(doc => {
          return {
            ...doc.data(),
            id: doc.id
          };
        });
        
        setReviews(reviewsData);
        generateChartData(reviewsData);
        generateTouristTypeData(reviewsData);
        generateTourPopularityData(reviewsData);
        generateTrendData(reviewsData, selectedTrendMetric);
      } catch (error) {
        console.error("Error fetching reviews: ", error);
      }
    };

    fetchReviews();
  }, [selectedTrendMetric]);

  // Generate tour comparison data when selected tours change
  useEffect(() => {
    if (reviews.length > 0 && selectedToursForComparison.length > 0) {
      generateTourComparisonData(reviews, selectedToursForComparison);
    }
  }, [reviews, selectedToursForComparison]);

  const generateChartData = (reviewsData) => {
    // Define the mapping between chart labels and form field names
    const labelFieldMapping = {
      'Overall Experience': 'overallExperience',
      'Tour Guide': 'tourGuide',
      'Bus Cleanliness': 'busCleanliness',
      'Tour Punctuality': 'tourPunctuality',
      'Attraction Quality': 'attractionQuality',
      'Value for Money': 'valueForMoney',
      'Staff Friendliness': 'staffFriendliness',
      'Safety Measures': 'safetyMeasures',
      'Overall Satisfaction': 'overallSatisfaction',
      'Likelihood to Recommend': 'likelihoodToRecommend'
    };

    const labels = Object.keys(labelFieldMapping);

    const data = labels.map(label => {
      const fieldName = labelFieldMapping[label];
      const total = reviewsData.reduce((sum, review) => {
        // Parse the value or default to 0 if not present
        const value = parseInt(review[fieldName] || '0');
        return sum + (isNaN(value) ? 0 : value);
      }, 0);
      
      return reviewsData.length > 0 ? total / reviewsData.length : 0;
    });

    setChartData({
      labels,
      datasets: [
        {
          label: 'Average Ratings',
          data,
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
      ],
    });
  };

  const generateTouristTypeData = (reviewsData) => {
    // Count different tourist types
    const touristTypes = {};
    
    reviewsData.forEach(review => {
      const type = review.touristType || 'Unknown';
      touristTypes[type] = (touristTypes[type] || 0) + 1;
    });
    
    setTouristTypeData({
      labels: Object.keys(touristTypes),
      datasets: [
        {
          label: 'Tourist Types',
          data: Object.values(touristTypes),
          backgroundColor: [
            'rgba(255, 99, 132, 0.6)',
            'rgba(54, 162, 235, 0.6)',
            'rgba(255, 206, 86, 0.6)',
            'rgba(75, 192, 192, 0.6)'
          ],
          borderColor: [
            'rgba(255, 99, 132, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 206, 86, 1)',
            'rgba(75, 192, 192, 1)',
          ],
          borderWidth: 1,
        },
      ],
    });
  };

  const generateTourPopularityData = (reviewsData) => {
    // Count the popularity of different tours
    const tours = {};
    
    reviewsData.forEach(review => {
      const tour = review.selectedTour || 'Unknown';
      tours[tour] = (tours[tour] || 0) + 1;
    });
    
    // Sort by popularity and take top 5
    const sortedTours = Object.entries(tours)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    
    setTourPopularityData({
      labels: sortedTours.map(([name]) => name),
      datasets: [
        {
          label: 'Tour Popularity',
          data: sortedTours.map(([, count]) => count),
          backgroundColor: [
            'rgba(153, 102, 255, 0.6)',
            'rgba(255, 159, 64, 0.6)',
            'rgba(255, 99, 132, 0.6)',
            'rgba(54, 162, 235, 0.6)',
            'rgba(255, 206, 86, 0.6)',
          ],
          borderColor: [
            'rgba(153, 102, 255, 1)',
            'rgba(255, 159, 64, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 206, 86, 1)',
          ],
          borderWidth: 1,
        },
      ],
    });
  };

  const generateTrendData = (reviewsData, metricName) => {
    // Create a mapping of readable metric names to field names
    const metricLabels = {
      'overallExperience': 'Overall Experience',
      'tourGuide': 'Tour Guide',
      'busCleanliness': 'Bus Cleanliness',
      'tourPunctuality': 'Tour Punctuality',
      'attractionQuality': 'Attraction Quality',
      'valueForMoney': 'Value for Money',
      'staffFriendliness': 'Staff Friendliness',
      'safetyMeasures': 'Safety Measures',
      'overallSatisfaction': 'Overall Satisfaction',
      'likelihoodToRecommend': 'Likelihood to Recommend'
    };

    // Organize by review index instead of time
    const valuesPerPeriod = 5; // Number of reviews per group
    const periods = [];
    const periodAverages = [];
    
    for (let i = 0; i < reviewsData.length; i += valuesPerPeriod) {
      const periodReviews = reviewsData.slice(i, i + valuesPerPeriod);
      const periodLabel = `Group ${periods.length + 1}`;
      periods.push(periodLabel);
      
      // Calculate average for this period
      const total = periodReviews.reduce((sum, review) => {
        const value = parseInt(review[metricName] || '0');
        return sum + (isNaN(value) ? 0 : value);
      }, 0);
      
      const average = periodReviews.length > 0 ? total / periodReviews.length : 0;
      periodAverages.push(average);
    }
    
    setTrendData({
      labels: periods,
      datasets: [
        {
          label: metricLabels[metricName] || metricName,
          data: periodAverages,
          fill: false,
          borderColor: 'rgb(75, 192, 192)',
          tension: 0.1,
          pointBackgroundColor: 'rgb(75, 192, 192)',
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgb(75, 192, 192)'
        }
      ]
    });
  };

  const generateTourComparisonData = (reviewsData, tourNames) => {
    // Define metrics to compare
    const metrics = {
      'overallExperience': 'Overall Experience',
      'tourGuide': 'Tour Guide', 
      'busCleanliness': 'Bus Cleanliness',
      'tourPunctuality': 'Tour Punctuality',
      'attractionQuality': 'Attraction Quality',
      'valueForMoney': 'Value for Money',
      'staffFriendliness': 'Staff Friendliness',
      'safetyMeasures': 'Safety Measures',
      'overallSatisfaction': 'Overall Satisfaction',
      'likelihoodToRecommend': 'Recommend'
    };

    // Calculate average for each tour and metric
    const tourDatasets = tourNames.map((tourName, index) => {
      // Filter reviews for this tour
      const tourReviews = reviewsData.filter(review => review.selectedTour === tourName);
      
      // Calculate averages for each metric
      const metricAverages = Object.entries(metrics).map(([metricKey]) => {
        const total = tourReviews.reduce((sum, review) => {
          const value = parseInt(review[metricKey] || '0');
          return sum + (isNaN(value) ? 0 : value);
        }, 0);
        
        return tourReviews.length > 0 ? total / tourReviews.length : 0;
      });

      // Get a color for this tour
      const colors = [
        'rgb(255, 99, 132)',
        'rgb(54, 162, 235)',
        'rgb(255, 206, 86)',
        'rgb(75, 192, 192)',
        'rgb(153, 102, 255)',
        'rgb(255, 159, 64)'
      ];
      
      return {
        label: tourName,
        data: metricAverages,
        backgroundColor: `${colors[index % colors.length]}33`,
        borderColor: colors[index % colors.length],
        borderWidth: 2,
        pointBackgroundColor: colors[index % colors.length],
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: colors[index % colors.length]
      };
    });

    setTourComparisonData({
      labels: Object.values(metrics),
      datasets: tourDatasets
    });
  };

  const handleTrendMetricChange = (event) => {
    setSelectedTrendMetric(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const toggleRowExpansion = (rowId) => {
    setExpandedRow(expandedRow === rowId ? null : rowId);
  };

  const handleTouristTypeFilter = (type) => {
    setFilterTouristType(type);
    setPage(1); // Reset to first page when filtering
  };

  const toggleTourForComparison = (tourName) => {
    setSelectedToursForComparison(prev => {
      if (prev.includes(tourName)) {
        return prev.filter(name => name !== tourName);
      } else {
        // Limit to max 5 tours for comparison
        if (prev.length >= 5) {
          return [...prev.slice(1), tourName];
        }
        return [...prev, tourName];
      }
    });
  };

  const handleComparisonViewChange = (event, newView) => {
    if (newView !== null) {
      setComparisonView(newView);
    }
  };

  // Get all unique tour names from reviews for comparison selection
  const allTourNames = [...new Set(reviews.map(review => review.selectedTour).filter(Boolean))];

  // Get available tourist types for filtering
  const availableTouristTypes = ['All', ...new Set(reviews.map(review => review.touristType).filter(Boolean))];

  // Filter reviews based on selected type
  const filteredReviews = reviews.filter(review => 
    filterTouristType === 'All' || review.touristType === filterTouristType
  );

  // Get current page of reviews
  const currentReviews = filteredReviews.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Calculate overall review stats
  const calculateAverageRating = (reviews) => {
    if (reviews.length === 0) return 0;
    const total = reviews.reduce((sum, review) => {
      const value = parseInt(review.overallSatisfaction || '0');
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
    return (total / reviews.length).toFixed(1);
  };

  const overallRating = calculateAverageRating(reviews);
  const totalReviews = reviews.length;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Customer Reviews
          </Typography>
          
          <Box display="flex" alignItems="center">
            <Box textAlign="center" mr={2}>
              <Typography variant="h3" component="p" color="primary">
                {overallRating}
              </Typography>
              <Rating value={parseFloat(overallRating)/2} precision={0.1} readOnly />
              <Typography variant="body2" color="text.secondary">
                {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="review view tabs">
            <Tab label="Table View" />
            <Tab label="Card View" />
          </Tabs>
        </Box>

        {/* Filter Controls */}
        <Box display="flex" alignItems="center" mb={2}>
          <FilterListIcon sx={{ mr: 1 }} />
          <Typography variant="body1" sx={{ mr: 2 }}>
            Filter by Tourist Type:
          </Typography>
          {availableTouristTypes.map(type => (
            <Chip 
              key={type} 
              label={type} 
              onClick={() => handleTouristTypeFilter(type)}
              color={filterTouristType === type ? "primary" : "default"}
              sx={{ mr: 1 }}
            />
          ))}
        </Box>

        {tabValue === 0 ? (
          <TableContainer component={Paper} elevation={0} variant="outlined">
            <Table aria-label="reviews table">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell />
                  <TableCell>Tourist Type</TableCell>
                  <TableCell>Tour</TableCell>
                  <TableCell align="center">Overall</TableCell>
                  <TableCell align="center">Guide</TableCell>
                  <TableCell align="center">Value</TableCell>
                  <TableCell align="center">Satisfaction</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentReviews.length > 0 ? (
                  currentReviews.map((review, index) => (
                    <React.Fragment key={review.id || index}>
                      <TableRow 
                        hover
                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer' }}
                        onClick={() => toggleRowExpansion(review.id || index)}
                      >
                        <TableCell>
                          <IconButton size="small">
                            {expandedRow === (review.id || index) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                            {review.touristType || "Unknown"}
                            {review.selectedCruiseLine && <Typography variant="caption" sx={{ ml: 1 }}>({review.selectedCruiseLine})</Typography>}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <TourIcon sx={{ mr: 1, color: 'secondary.main' }} />
                            {review.selectedTour || "Unknown"}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={review.overallExperience || "N/A"} 
                            color={review.overallExperience >= 8 ? "success" : review.overallExperience >= 5 ? "primary" : "error"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={review.tourGuide || "N/A"} 
                            color={review.tourGuide >= 8 ? "success" : review.tourGuide >= 5 ? "primary" : "error"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={review.valueForMoney || "N/A"} 
                            color={review.valueForMoney >= 8 ? "success" : review.valueForMoney >= 5 ? "primary" : "error"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={review.overallSatisfaction || "N/A"} 
                            color={review.overallSatisfaction >= 8 ? "success" : review.overallSatisfaction >= 5 ? "primary" : "error"}
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                          <Collapse in={expandedRow === (review.id || index)} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                              <Typography variant="h6" gutterBottom component="div">
                                Complete Ratings
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Box mb={1}>
                                    <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
                                      Bus Cleanliness:
                                    </Typography>
                                    <Chip size="small" label={review.busCleanliness || "N/A"} />
                                  </Box>
                                  <Box mb={1}>
                                    <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
                                      Tour Punctuality:
                                    </Typography>
                                    <Chip size="small" label={review.tourPunctuality || "N/A"} />
                                  </Box>
                                  <Box mb={1}>
                                    <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
                                      Attraction Quality:
                                    </Typography>
                                    <Chip size="small" label={review.attractionQuality || "N/A"} />
                                  </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Box mb={1}>
                                    <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
                                      Staff Friendliness:
                                    </Typography>
                                    <Chip size="small" label={review.staffFriendliness || "N/A"} />
                                  </Box>
                                  <Box mb={1}>
                                    <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
                                      Safety Measures:
                                    </Typography>
                                    <Chip size="small" label={review.safetyMeasures || "N/A"} />
                                  </Box>
                                  <Box mb={1}>
                                    <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
                                      Likelihood to Recommend:
                                    </Typography>
                                    <Chip size="small" label={review.likelihoodToRecommend || "N/A"} />
                                  </Box>
                                </Grid>
                                {Object.entries(review).filter(([key]) => key.endsWith('Comments') && review[key]).map(([key, value]) => (
                                  <Grid item xs={12} key={key}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                      {key.replace('Comments', ' Comments')}:
                                    </Typography>
                                    <Typography variant="body2" paragraph>
                                      "{value}"
                                    </Typography>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body1">No reviews available.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Grid container spacing={3}>
            {currentReviews.length > 0 ? (
              currentReviews.map((review, index) => (
                <Grid item xs={12} sm={6} md={4} key={review.id || index}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Chip
                          icon={<PersonIcon />}
                          label={review.touristType || "Unknown"}
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          label={`${review.overallSatisfaction || "?"}/10`}
                          color={review.overallSatisfaction >= 8 ? "success" : review.overallSatisfaction >= 5 ? "primary" : "error"}
                        />
                      </Box>
                      
                      <Typography variant="h6" gutterBottom>
                        <TourIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                        {review.selectedTour || "Unknown Tour"}
                      </Typography>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} mb={2}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Experience</Typography>
                          <Typography variant="body2">{review.overallExperience || "N/A"}/10</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Guide</Typography>
                          <Typography variant="body2">{review.tourGuide || "N/A"}/10</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Value</Typography>
                          <Typography variant="body2">{review.valueForMoney || "N/A"}/10</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Cleanliness</Typography>
                          <Typography variant="body2">{review.busCleanliness || "N/A"}/10</Typography>
                        </Box>
                      </Box>
                      
                      <Box display="flex" alignItems="center" mt={2}>
                        <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          Recommend: {review.likelihoodToRecommend || "N/A"}/10
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography variant="body1" align="center">No reviews available.</Typography>
              </Grid>
            )}
          </Grid>
        )}

        {filteredReviews.length > rowsPerPage && (
          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination 
              count={Math.ceil(filteredReviews.length / rowsPerPage)} 
              page={page} 
              onChange={handlePageChange} 
              color="primary" 
            />
          </Box>
        )}
      </Paper>

      <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 4 }}>
        Analytics Dashboard
      </Typography>

      <Grid container spacing={4}>
        {/* Tour Comparison Section */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5" component="h3" sx={{ display: 'flex', alignItems: 'center' }}>
                <CompareArrowsIcon sx={{ mr: 1 }} />
                Tour Comparison
              </Typography>
              <ToggleButtonGroup
                value={comparisonView}
                exclusive
                onChange={handleComparisonViewChange}
                size="small"
              >
                <ToggleButton value="radar">Radar View</ToggleButton>
                <ToggleButton value="bar">Bar View</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            
            <Typography variant="body1" sx={{ mb: 2 }}>
              Select up to 5 tours to compare their performance across different metrics:
            </Typography>
            
            <Box mb={3}>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                {allTourNames.map((tourName) => (
                  <Chip
                    key={tourName}
                    label={tourName}
                    onClick={() => toggleTourForComparison(tourName)}
                    color={selectedToursForComparison.includes(tourName) ? "primary" : "default"}
                    variant={selectedToursForComparison.includes(tourName) ? "filled" : "outlined"}
                    sx={{ mb: 1 }}
                  />
                ))}
              </Stack>
            </Box>
            
            {selectedToursForComparison.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">Select tours above to see comparison</Typography>
              </Box>
            ) : (
              <Box height={400}>
                {comparisonView === 'radar' ? (
                  <Radar 
                    data={tourComparisonData}
                    options={{ 
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        r: {
                          min: 0,
                          max: 10,
                          ticks: {
                            stepSize: 2
                          }
                        }
                      },
                      elements: {
                        line: {
                          borderWidth: 2
                        }
                      }
                    }}
                  />
                ) : (
                  <Bar 
                    data={tourComparisonData}
                    options={{ 
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          max: 10,
                          title: {
                            display: true,
                            text: 'Average Rating (0-10)'
                          }
                        },
                        x: {
                          ticks: {
                            autoSkip: false,
                            maxRotation: 45,
                            minRotation: 45
                          }
                        }
                      }
                    }}
                  />
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Average Ratings Chart */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h5" component="h3" gutterBottom>
              Average Ratings by Category
            </Typography>
            <Box height={400}>
              <Bar 
                data={chartData} 
                options={{ 
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 10,
                      title: {
                        display: true,
                        text: 'Average Rating (0-10)'
                      }
                    },
                    x: {
                      ticks: {
                        autoSkip: false,
                        maxRotation: 45,
                        minRotation: 45
                      }
                    }
                  },
                  plugins: { 
                    legend: { position: 'top' }, 
                    title: { display: true, text: 'Average Ratings of Reviews' } 
                  }
                }} 
              />
            </Box>
          </Paper>
        </Grid>

        {/* Trend Analysis Chart */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" component="h3">
                Rating Trends
              </Typography>
              <FormControl sx={{ width: 250 }}>
                <InputLabel id="metric-select-label">Metric</InputLabel>
                <Select
                  labelId="metric-select-label"
                  id="metric-select"
                  value={selectedTrendMetric}
                  label="Metric"
                  onChange={handleTrendMetricChange}
                >
                  <MenuItem value="overallExperience">Overall Experience</MenuItem>
                  <MenuItem value="tourGuide">Tour Guide</MenuItem>
                  <MenuItem value="busCleanliness">Bus Cleanliness</MenuItem>
                  <MenuItem value="tourPunctuality">Tour Punctuality</MenuItem>
                  <MenuItem value="attractionQuality">Attraction Quality</MenuItem>
                  <MenuItem value="valueForMoney">Value for Money</MenuItem>
                  <MenuItem value="staffFriendliness">Staff Friendliness</MenuItem>
                  <MenuItem value="safetyMeasures">Safety Measures</MenuItem>
                  <MenuItem value="overallSatisfaction">Overall Satisfaction</MenuItem>
                  <MenuItem value="likelihoodToRecommend">Likelihood to Recommend</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box height={400}>
              <Line 
                data={trendData} 
                options={{ 
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 10,
                      title: {
                        display: true,
                        text: 'Average Rating (0-10)'
                      }
                    }
                  },
                  plugins: { 
                    legend: { position: 'top' },
                    title: { display: true, text: 'Rating Trends' }
                  }
                }} 
              />
            </Box>
          </Paper>
        </Grid>

        {/* Tourist Type Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h5" component="h3" gutterBottom>
              Tourist Type Distribution
            </Typography>
            <Box height={400}>
              <Pie 
                data={touristTypeData} 
                options={{ 
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { 
                    legend: { position: 'right' }, 
                    title: { display: true, text: 'Distribution of Tourist Types' } 
                  }
                }} 
              />
            </Box>
          </Paper>
        </Grid>

        {/* Tour Popularity Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h5" component="h3" gutterBottom>
              Most Popular Tours
            </Typography>
            <Box height={400}>
              <Doughnut 
                data={tourPopularityData} 
                options={{ 
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { 
                    legend: { position: 'right' }, 
                    title: { display: true, text: 'Top 5 Most Reviewed Tours' } 
                  }
                }} 
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default FormReviews;